// ============================================================================
// Equipment lifecycle intelligence (EQUIP-01, 2026-05-28).
//
// ESC's Equip table is messy: duplicates (same unit entered twice), install
// MATERIALS dumped in as "equipment" (pipe / duct / fittings), and old units
// left in the list after replacement. This module turns the raw rows into a
// clean, classified view for the Customer 360 Equipment box.
//
// DESIGN STANCE (high-stakes — a tech relies on this): SUGGEST, never silently
// hide. Everything is grouped + always expandable; uncertain rows fall through
// to a visible "Other / uncategorized" bucket rather than disappearing. The
// heuristics below are intentionally CONSERVATIVE — we'd rather show a part as
// equipment than hide a real unit as a part.
//
// All heuristic — no AI. The AI layer (MIG-NOTES-01 notes extraction, fuzzy
// model matching, AI-07 data-plate OCR) enriches this later. The keyword /
// model-prefix tables are meant to be tuned over time from real data.
// ============================================================================

export interface EquipmentRow {
  counter: string;
  mfg: string | null;
  model: string | null;
  serial: string | null;
  eq_type: string | null;
  equip_loc: string | null;
  install_date: string | null; // YYYY-MM-DD
  warranty_date: string | null;
  ser_agr_no: string | null;
  notes: string | null;
  // Custom fields sometimes carry warranty TERMS (e.g. water heater:
  // custom_1 "1 Year" labor, custom_2 "6 Years" parts). Read for warranty.
  custom_1: string | null;
  custom_2: string | null;
  invoice: string | null; // ESC invoice that installed this unit (links to mirror_sales)
}

export type EquipTier = "system" | "auxiliary" | "consumable" | "material" | "other";

export type EquipCategory =
  | "furnace" | "ac" | "coil" | "heat-pump" | "mini-split" | "rooftop" | "magic-pak"
  | "garage-heater" | "air-handler" | "boiler"
  | "water-heater" | "humidifier" | "dehumidifier" | "uv" | "air-cleaner"
  | "thermostat" | "erv-hrv" | "rds"
  | "consumable" | "material" | "other";

export interface ClassifiedEquip extends EquipmentRow {
  tier: EquipTier;
  category: EquipCategory;
  categoryLabel: string;
  installYear: number | null;
  warrantyLabor: string | null;   // YYYY-MM-DD, parsed from notes
  warrantyParts: string | null;
  warrantyEffective: string | null; // best usable warranty date, or null if unknown/junk
  /** counter of the record this duplicates (same serial, less complete). */
  duplicateOf?: string;
  /** when a newer same-category unit supersedes this one. */
  replaced?: boolean;
  replacedByModel?: string | null;
  replacedDate?: string | null;
  /** human override (EQUIP-LIFECYCLE-02): the office marked it removed/active. */
  replacedReason?: string | null;
  overrideSet?: boolean;
  /** MIRROR-EQUIP-DELETE-01: the ESC sync stopped returning this row — it was deleted
   *  (or re-keyed) in ESC. Marked automatically by pegasus_equipment_esc_delete_sweep. */
  deletedInEsc?: boolean;
  /** DOUBLE-ENTRY (34i83): this unit also appears on an earlier invoice — the
   *  sale — folded into it; the earlier record's install_date / invoice. */
  soldDate?: string | null;
  soldInvoice?: string | null;
}

/** Pegasus-native lifecycle override on an ESC unit (keyed by counter).
 *  EQUIP-EDIT-FIX-01: status is OPTIONAL — a row that only corrects fields
 *  (model / serial / room) carries no lifecycle opinion (null), and the
 *  derived active/replaced guess stands. Lifecycle is only ever set by the
 *  explicit Mark removed / Mark active buttons. */
export interface EquipOverride {
  esc_counter: string;
  status?: "removed" | "active" | null;
  removal_date?: string | null;
  removal_reason?: string | null;
  replaced_by_model?: string | null;
  // Pegasus-native field corrections (null = use the ESC value).
  mfg?: string | null;
  model?: string | null;
  serial?: string | null;
  install_date?: string | null;
  eq_type?: string | null;
  on_plan?: boolean | null;
  /** ROOM-EDIT-01: the office's say on which room the unit serves. */
  equip_loc?: string | null;
}

export interface EquipmentView {
  systems: ClassifiedEquip[];
  auxiliary: ClassifiedEquip[];
  consumables: ClassifiedEquip[];
  materials: ClassifiedEquip[];
  replaced: ClassifiedEquip[]; // superseded units + duplicates (the collapsible bucket)
  counts: { systems: number; auxiliary: number; consumables: number; materials: number; replaced: number };
}

export const CATEGORY_LABEL: Record<EquipCategory, string> = {
  furnace: "Furnace", ac: "Air Conditioner", coil: "Evaporator Coil",
  "heat-pump": "Heat Pump", "mini-split": "Mini-Split", rooftop: "Rooftop Unit",
  "magic-pak": "Magic-Pak / PTAC", "garage-heater": "Garage / Unit Heater",
  "air-handler": "Air Handler", boiler: "Boiler",
  "water-heater": "Water Heater", humidifier: "Humidifier",
  dehumidifier: "Dehumidifier", uv: "UV / Air Purifier", "air-cleaner": "Air Cleaner",
  thermostat: "Thermostat", "erv-hrv": "ERV / HRV", rds: "Refrigerant Detection (RDS)",
  consumable: "Consumable", material: "Install Material", other: "Equipment",
};

// --- ADMIN-EDIT-01: runtime registry for admin-added categories -------------
// The `equipment_category` table extends the classifier's vocabulary without
// code changes (Seth: "I need to run Pegasus without constant tweakings").
// Builtins above stay the rule-classifier's vocabulary; admin customs are
// registered here at app load (useSyncEquipmentCategories) so labels and
// system/auxiliary grouping resolve everywhere equipment renders.
let CUSTOM_CATEGORIES = new Map<
  string,
  { label: string; grouping: "system" | "auxiliary" | "other" }
>();

export function setCustomEquipmentCategories(
  rows: { key: string; label: string; grouping: "system" | "auxiliary" | "other" }[],
) {
  CUSTOM_CATEGORIES = new Map(rows.map((r) => [r.key, { label: r.label, grouping: r.grouping }]));
}

/** Label for ANY category key — builtin table first, then admin customs. */
export function categoryLabelFor(category: string): string {
  return (
    (CATEGORY_LABEL as Record<string, string>)[category] ??
    CUSTOM_CATEGORIES.get(category)?.label ??
    "Equipment"
  );
}

function customGrouping(category: string): "system" | "auxiliary" | "other" | null {
  return CUSTOM_CATEGORIES.get(category)?.grouping ?? null;
}

export const SYSTEM_CATEGORIES = new Set<EquipCategory>([
  "furnace", "ac", "coil", "heat-pump", "mini-split", "rooftop", "magic-pak",
  "garage-heater", "air-handler", "boiler", "water-heater",
]);
export const AUX_CATEGORIES = new Set<EquipCategory>([
  "humidifier", "dehumidifier", "uv", "air-cleaner", "thermostat", "erv-hrv", "rds",
]);

// --- Equipment series / product tier (Lennox + Armstrong) -------------------
// Sedgwick's lines map to a quality tier encoded in the model prefix:
//   Lennox  SL / SLP -> Signature (premium), EL -> Elite (mid), ML -> Merit (entry)
//   Armstrong (Allied Air) A9_/A8_/A7_/4SCU  -> Armstrong line
// Works across furnaces, ACs, and heat pumps (EL297 furnace + EL16KC1 AC are
// both Elite). Coils/accessories carry no tier -> "other". This is the same
// Good/Better/Best spine that membership tiers (AGR) and ordering reuse.
export type EquipSeries = "signature" | "elite" | "merit" | "armstrong" | "other";

export const SERIES_LABEL: Record<EquipSeries, string> = {
  signature: "Signature (premium)",
  elite: "Elite (mid)",
  merit: "Merit (entry)",
  armstrong: "Armstrong",
  other: "Other / no tier",
};

// --- Base model (strip the manufacturer dash/revision suffix) ---------------
// Lennox (and others) append a REVISION number to the model that changes when
// the factory makes a minor engineering change to the same unit — e.g.
// SL280UH070V36A-09 and SL280UH070V36A-05 are the SAME furnace, just different
// build revisions. For UNIT demand / inventory (how many of this furnace to
// order) they must collapse to one. (The revision still matters for ordering a
// PART for a specific unit — that's a different use case, handled elsewhere.)
//
// Two suffix shapes seen in the data:
//   1. dash-revision: trailing "-NN"  → SL280UH070V36A-09, CK40CT-48C-71
//   2. voltage-glued revision on condensers/coils: a 1-2 digit rev stuck onto
//      the 208/230/460 voltage code → EL16XC1-024-230A05  (rev "05")
// We strip both, but the voltage rule REQUIRES a real voltage prefix so we
// never accidentally merge distinct codes (e.g. RDS boards 27A02 / 27A05 stay
// separate — they aren't a voltage-glued revision).
export function baseModel(model: string | null): string {
  let m = (model ?? "").trim().toUpperCase();
  if (!m) return "";
  m = m.replace(/-\d{1,2}$/, "");                       // SL280...V36A-09 -> ...V36A
  m = m.replace(/(208|230|460)([A-Z])\d{1,2}$/, "$1$2"); // ...-230A05 -> ...-230A
  return m;
}

// --- Stock-match key --------------------------------------------------------
// The Sales INSTOCK sheet writes models DIFFERENTLY than ESC/install history:
//   install "SL280UH070V36A-09"  vs  stock "SL280UH070V36AK"   (K = R-454B)
//   install "ML13KC1-030-230A02" vs  stock "ML13KC1030"        (no voltage/rev)
//   install "CK40CT-24A-71"      vs  stock "CK40CT24A71"        (cabinet code)
// coreModel() reduces an INSTALL model to a key that matches the stock snapshot:
// drop the dash-revision (via baseModel) + dashes, then the trailing
// voltage+rev tail (230A02) and the R-454B "K". Stock-side cores are seeded to
// match this output (see stock_on_hand). Furnaces + condensers match cleanly;
// coil cabinet codes are reconciled at seed time. Honest caveat: this is a
// best-effort key, not a guaranteed 1:1 — match rate is surfaced in the report.
export function coreModel(model: string | null): string {
  let m = baseModel(model);                          // strip -NN revision first
  m = m.toUpperCase().replace(/[^A-Z0-9]/g, "");      // alphanumeric only
  m = m.replace(/(208|230|460)[A-Z]?\d{0,2}$/, "");   // drop voltage+rev tail
  m = m.replace(/K$/, "");                            // drop R-454B marker
  return m;
}

// --- Capacity (tonnage) for condensers / heat pumps ------------------------
// AC + heat-pump models carry a 3-digit capacity code after the refrigerant
// token (XC1/KC1/XP1/KP1, or bare XC/KC/XP/KP); tonnage = code / 12 (024 = 2.0
// ton, 030 = 2.5, 036 = 3.0, ...). Used by the seasonal-demand "by size" rollup
// so a buyer sees "EL16 · 2.0 ton" across years REGARDLESS of the R-410A (XC) →
// R-454B (KC) refrigerant switch. Returns null when there's no capacity code
// (furnaces size by BTU, not tonnage) so the report can bucket those honestly.
export function condenserTons(model: string | null): number | null {
  const m = (model ?? "").toUpperCase().replace(/\s/g, "");
  const mm = m.match(/(?:XC1|KC1|XP1|KP1|XC|KC|XP|KP)S?-?(\d{3})/);
  if (!mm) return null;
  const code = parseInt(mm[1], 10);
  if (code < 12 || code > 72) return null; // 1–6 ton sanity bound
  return Math.round((code / 12) * 2) / 2;
}

// The refrigerant-AGNOSTIC family prefix (letters + 2 digits): EL16KC1 → "EL16",
// EL16XC1 → "EL16", ML13KC1 → "ML13". Lets the by-size rollup fold the older
// R-410A unit and its R-454B replacement into ONE family row so the multi-year
// trend is real instead of artificially split by the refrigerant changeover.
export function modelFamily(model: string | null): string {
  const m = (model ?? "").toUpperCase().replace(/\s/g, "");
  const mm = m.match(/^([A-Z]{2,3}\d{2})/);
  return mm ? mm[1] : "";
}

export function deriveSeries(mfg: string | null, model: string | null): EquipSeries {
  const mf = (mfg ?? "").toUpperCase();
  const m = (model ?? "").toUpperCase().trim();
  if (mf.includes("ARMSTRONG") || /^(A9|A8|A7|A6|4SCU)/.test(m)) return "armstrong";
  if (/^SLP?\d/.test(m)) return "signature"; // SL280, SLP99
  if (/^EL\d/.test(m)) return "elite"; // EL297, EL16KC1
  if (/^ML\d/.test(m)) return "merit"; // ML180, ML13KC1
  return "other";
}

function hay(row: EquipmentRow): string {
  return `${row.mfg ?? ""} ${row.model ?? ""} ${row.eq_type ?? ""} ${row.notes ?? ""}`.toUpperCase();
}

// --- Consumable detection (check BEFORE equipment — a pad is not a humidifier) ---
function isConsumable(row: EquipmentRow): boolean {
  const h = hay(row);
  if (/\bWATER PANEL\b|\bEVAP(ORATOR)? PAD\b|\bHUMIDIFIER PAD\b|\bPAD\b/.test(h)) return true;
  if (/\bAPR\s?-?\s?\d{2}\b|\b#?\s?(10|12|35|45)\b.*\bPAD\b/.test(h)) return true; // Aprilaire pad model #s
  if (/\bBULB\b|\bLAMP\b|UV BULB|GERMICIDAL/.test(h) && !/SYSTEM|PURIFIER|PUREAIR|REME/.test(h)) return true;
  if (/BREATHECLEAN/.test(h) && /BULB|LAMP|REPLACEMENT/.test(h)) return true;
  if (/\bFILTER\b|\bMERV\b|MEDIA FILTER/.test(h) && !/AIR CLEANER|CABINET|HOUSING/.test(h)) return true;
  if (/\bBELT\b/.test(h)) return true;
  return false;
}

// --- Material detection (conservative: only with strong "this is a part" signals) ---
const MATERIAL_WORDS =
  /\bB-?VENT\b|\bFLEX DUCT\b|\bLINESET\b|\bCANVAS\b|\bELBOW\b|\bELLS?\b|\bREGISTER\b|\bCOLLAR\b|\bTRANSITION\b|\bADAPTER\b|\bGALV\b|\bALUM(INUM)? PIPE\b|\bR\/A\b|\bW\/A\b|FRESH AIR HOOD|\bTEE( CAP)?\b|\bDROP\b|\bPIPE\b|\bDUCT\b/;
function isMaterial(row: EquipmentRow): boolean {
  // Kits / conversion parts are not equipment — they belong in the future
  // parts/inventory module, not the equipment list (Seth 2026-05-31: "LP Kit
  // are more parts"). e.g. Lennox "LP KIT" (propane conversion kit).
  if (/\bLP\s*KIT\b|CONVERSION KIT|\bLP CONVERSION\b/.test(hay(row))) return true;
  // Strong structural-material words => material even WITH a model/serial. ESC
  // sometimes records duct/vent line-items as "equipment" with a model+serial
  // (e.g. "Z DUCT", model "IAQ"). \bDUCT\b is word-bounded so it won't catch
  // "ductless". Real systems (furnace/AC/coil/water-heater) don't carry these.
  if (MATERIAL_WORDS.test(hay(row))) return true;
  // Otherwise: no serial AND no model AND a part-number-ish mfg (e.g. "4RV-3").
  const hasSerial = !!row.serial && row.serial.trim().length > 0;
  const hasModel = !!row.model && row.model.trim().length > 0;
  const mfg = (row.mfg ?? "").trim().toUpperCase();
  const mfgLooksLikePartCode = /^\d+$/.test(mfg) || /^[0-9]+[A-Z]+\d*$/.test(mfg) || /^\d?[A-Z]{1,3}-?\w{0,4}$/.test(mfg);
  if (!hasSerial && !hasModel && mfgLooksLikePartCode) return true;
  // Service chemicals in a can/bottle (sealant, stop-leak, dye, refrigerant by
  // the ounce): the REAL description lives only in NOTES while ESC jams a part #
  // into mfg (e.g. mfg "B85-597" / notes "Easy Seal 6 Oz Can (stop Leak)"). These
  // are parts, not equipment. Guarded by no-model/no-serial so a real unit whose
  // notes mention "8 oz" of charge is never mis-bucketed.
  const CHEMICAL = /\bSTOP\s*-?\s*LEAK\b|\bEASY\s*-?\s*SEAL\b|\bLEAK\s*(SEAL|STOP|FREEZE)\b|\bSEALANT\b|\bLEAK\s*LOCK\b|\bNYLOG\b|\bDYE\b|\b\d+\s*OZ\b/;
  if (!hasSerial && !hasModel && CHEMICAL.test(hay(row))) return true;
  return false;
}

// --- Equipment category ---
/** Ductless evidence: a model prefix anywhere, or a ductless-only brand on the
 *  unit's own fields (SKILL-STEER-01 — see the model rules below). */
function looksMiniSplit(row: EquipmentRow, h: string): boolean {
  if (/MINI.?SPLIT|DUCTLESS|\bMSZ|\bMUZ|\bMUY|\bMXZ|\bMSY|\bFTX|\bFTK|\bCTX|\bRX[SL]|\bRX[BFNT]\d|\bRK[BFN]\d|\b[2-5]MX[LS]|\bFJA[OS]U|\bA[OS]UG?\d|\bA[GR]U\d|\bLM[NU]\d|\bLMCN\d|\bLS[NU]\d|\bAQV\d|\b\d?(VIR|LIV)\d{2}HP/.test(h)) return true;
  const unitWords = `${row.mfg ?? ""} ${row.model ?? ""} ${row.eq_type ?? ""}`.toUpperCase();
  return /MITSUBISHI|\bGREE\b|FUJI(TSU|STU)|\bSAMSUNG\b/.test(unitWords);
}

function detectCategory(row: EquipmentRow): EquipCategory {
  const h = hay(row);
  const model = (row.model ?? "").toUpperCase();

  // ESC's own eq_type is the most reliable signal when present — the office
  // categorized it. Trust it before guessing from the model string (the
  // model-only AI fallback mislabels things like a Bryant 581 package unit).
  const et = (row.eq_type ?? "").trim().toUpperCase();
  if (et) {
    if (/ROOFTOP|^RTU\b|PACKAGE/.test(et)) return "rooftop";
    if (/BOILER/.test(et)) return "boiler";
    if (/UNIT\s*HEATER|GARAGE/.test(et)) return "garage-heater";
    if (/WATER\s*HEATER|WTR\s*HTR/.test(et)) return "water-heater";
    if (/DEHUMID/.test(et)) return "dehumidifier";
    if (/HUMIDIF/.test(et)) return "humidifier";
    if (/\bHRV\b|\bERV\b|RECOVERY|VENTILAT/.test(et)) return "erv-hrv";
    // The office's own words for ductless, from Sedgwick's list (SKILL-STEER-01,
    // 2026-09-23): "Mini split", "Mini w 2 heads", "Head - main flo", "Mini
    // cassette", "Min split", "Fujutsu Mini Sp", "Evaporator Head", "Heat pump
    // mini" — ahead of HEAT PUMP and the coil's EVAP, which would grab them.
    if (/\bMINI\b|MINI.?SPLIT|\bMIN SPLIT\b|DUCTLESS|^HEADS?\b|EVAP\w*\s+HEADS?\b|CASSETTE|F[IU]J[IU]T?SU/.test(et)) return "mini-split";
    if (/HEAT\s*PUMP/.test(et)) return "heat-pump";
    if (/MAGIC.?PAK|PTAC/.test(et)) return "magic-pak";
    if (/THERMOSTAT|TSTAT/.test(et)) return "thermostat";
    if (/A-?COIL|^COIL$|EVAP/.test(et)) return "coil";
    if (/CONDENSER|^A\.?\/?C\b|^AC\b/.test(et)) {
      // ESC loosely tags some heat pumps / mini-splits as "A/C" — let the
      // clearer model/notes signal win before falling back to straight AC.
      if (/HEAT\s*PUMP|\bHP\b/.test(h)) return "heat-pump";
      if (looksMiniSplit(row, h)) return "mini-split";
      return "ac";
    }
    if (/\bEAC\b|AIR\s*CLEAN|APRILAIRE/.test(et)) return "air-cleaner";
    if (/\bUV\b|PURIF/.test(et)) return "uv";
    if (/^FILTER$/.test(et)) return "consumable";
    if (/FURNACE|FORCED\s*AIR|GAS\s*F/.test(et)) return "furnace";
    // anything else (e.g. "Fireplace") falls through to the model rules / other
  }

  // Water heaters
  if (/WATER HEATER|WTR HTR|WATER HTR|BRADFORD\s*WHITE|\bA\.?O\.?\s*SMITH\b|\bRG\d|RHEEM.*\bGAL|\bNAVIEN\b|\bNORITZ\b/.test(h)) return "water-heater";

  // RDS — Refrigerant Detection System boards (R-454B / A2L mandate, 2025+). A
  // field kit added to a furnace during a new AC install; an orderable safety
  // PART, not a system. Sedgwick's 27A0x codes = RDS boards (Seth 2026-05-31).
  if (/\b27A0\d\b/.test(model) || /\bRDS\b|REFRIGERANT DETECT|LEAK DETECT|A2L (SENSOR|DETECT|KIT)|MITIGATION (KIT|SENSOR)/.test(h)) return "rds";

  if (/DEHUMID/.test(h)) return "dehumidifier";
  // Aprilaire humidifiers are the 6xx/7xx/8xx series; 2xxx/5xxx are air cleaners.
  if (/HUMIDIFIER|STEAM HUMID|APRILAIRE\s*[678]\d\d|\bAPR\s*[678]\d\d\b/.test(h)) return "humidifier";
  if (/\bUV\b|GERMICIDAL|PUREAIR|PURE AIR|\bREME\b|AIR PURIF|IWAVE|PHOTOCAT|AIR ?SCRUBBER/.test(h)) return "uv";
  if (/AIR CLEANER|MEDIA (AIR )?CLEANER|ALTERAIRE|HEALTHY CLIMATE|PERFECT ?16|ELECTRONIC AIR|BREATHE\s*CLEAN|APRILAIRE\s*[25]\d\d\d|\bAPR[25]\d\d\d\b/.test(h)) return "air-cleaner";
  if (/\bERV\b|\bHRV\b|VENTILAT|HEAT RECOVERY|ENERGY RECOVERY|\bVAUTOW?\b|\bB\d{3}[EH]\d{2}/.test(h)) return "erv-hrv";
  if (/THERMOSTAT|\bTSTAT\b|COMFORTSENSE|\bTH\d{3,4}\b|BRAEBURN|ECOBEE|\bNEST\b|HONEYWELL\s*T\d|\bVP\d{4}\b|PRESTIGE|\bTHX\d|\bYTHM|\bS[34]0\b/.test(h)) return "thermostat";

  // Air handler BEFORE furnace: CBK/CBX/CBA contain "UH" (e.g. CBK45UHET) but
  // are air handlers, not furnaces (Seth 2026-05-31).
  if (/AIR HANDLER|\bAHU\b|FAN COIL|\bCB[KXA]\d/.test(h)) return "air-handler";

  // Mini-splits by model prefix, from the units actually on file (SKILL-STEER-01):
  // Mitsubishi MSZ/MUZ/MXZ/MSY · Daikin FTX/FTK/CTX/RX_/RK_/3MXL/4MXS ·
  // Fujitsu ASU/AOU/AGU/ARU (+ the FJ- prefix and the office's spellings) ·
  // LG LMU/LMN/LMCN/LSN/LSU · Samsung · Gree/Greentek VIR/LIV. The word DAIKIN
  // alone no longer decides — Daikin makes furnaces too (a DM96 with no type
  // was filing as a mini-split). The brand words count on the unit's own
  // fields, not the notes — a part row reads "CAPACITOR (SAMSUNG)" there.
  if (looksMiniSplit(row, h)) return "mini-split";

  // Magic-Pak / PTAC — wall-mounted packaged terminal unit (heat+cool+sometimes
  // vent in one box). Its own category, NOT rooftop (Seth 2026-05-31). Magic-Pak
  // HWC/MHC model lines; generic PTAC.
  if (/MAGIC.?PAK|\bHWC\d|\bMHC\d|\bPTAC\b|PACKAGE TERMINAL/.test(h)) return "magic-pak";

  // ...incl. Bryant/Carrier packaged-unit model nomenclature (Bryant 580–583,
  // Carrier 48/49/50 series) — these are rooftop package units, not furnaces.
  if (
    /ROOFTOP|\bRTU\b|PACKAGE[D]?\s*UNIT|GAS\s*\/\s*ELEC|VOYAGER|PRECEDENT/.test(h) ||
    /^(58[0-3]|4[89][A-Z]|50[A-Z])/.test(model)
  )
    return "rooftop";
  if (/HEAT PUMP|\bXP\d/.test(h)) return "heat-pump";
  if (/BOILER/.test(h)) return "boiler";

  // Garage / unit heaters — own category, BEFORE furnace (Seth 2026-05-31:
  // Reznor UDX, Modine HD, Lennox LF were being mislabeled furnace). Suspended
  // gas-fired heaters for garages/shops/warehouses, not central furnaces.
  if (/GARAGE HEATER|UNIT HEATER|\bUDX\b|\bUDAP\b|\bUDAS\b|\bMODINE\b|\bHD\d{2,3}\b|\bHDS\d|\bLF\d{2}\b|REZNOR/.test(h)) return "garage-heater";

  // Furnace BEFORE AC: orientation infix (UH=upflow, DF=downflow, US=upflow) +
  // BTU, the FURNACE keyword, or the Lennox G-series. Lennox furnace models
  // start with EL2/ML1/SL, which the OLD broad AC letter-prefix rule wrongly
  // grabbed — so furnaces were counting as A/C (the "1 furnace" bug). Fixed.
  // EQUIP-AREA-LIFE-01 companion: variable-speed infixes (UHV/DFV — Gordon's
  // G60UHV36) carry a letter between the orientation code and the BTU digits.
  if (/FURNACE|GAS FURNACE|\bFURN\b/.test(h) || /(UH|DF|US)[A-Z]?\d|\bG\d{2}\b/.test(model)) return "furnace";

  // AC condenser — explicit condenser signals only (broad letter-prefix removed).
  // KC = R-454B condenser (ML13KC1); XC/ACX legacy; SCU / A#AC# = Armstrong.
  if (/KC\d|XC\d|\dACX|\bACX\d|\bSCU\d|\bA\d?AC\d|CONDENS|AIR COND|\bA\/C\b|SEER/.test(h)) return "ac";

  // Evaporator coil — Lennox CK (CK40), Armstrong #EC# / EAC#, generic.
  if (/\bCOIL\b|EVAP|CASED|\bCK\d|\bCR\d|\bLC\d|\b\dEC\d|\bEAC\d|\bC[HX]\d/.test(h)) return "coil";

  return "other";
}

function tierFor(cat: EquipCategory): EquipTier {
  if (SYSTEM_CATEGORIES.has(cat)) return "system";
  if (AUX_CATEGORIES.has(cat)) return "auxiliary";
  // Admin-added categories group per their table row (ADMIN-EDIT-01).
  const custom = customGrouping(cat);
  if (custom === "system") return "system";
  if (custom === "auxiliary") return "auxiliary";
  return "other";
}

// --- Warranty parse ---
function parseDateLoose(s: string): string | null {
  // "5/22/2028" -> "2028-05-22"
  const m = s.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (m) return `${m[3]}-${m[1].padStart(2, "0")}-${m[2].padStart(2, "0")}`;
  return null;
}
// "1 Year" / "6 Years" + an install date -> absolute expiry. Guarded by the
// "N year" shape so non-warranty custom fields (filter size, belt) are ignored.
function termToExpiry(term: string | null, install: string | null): string | null {
  if (!term || !install) return null;
  const m = term.toUpperCase().match(/(\d+)\s*YEAR/);
  if (!m) return null;
  const parts = install.split("-");
  if (parts.length !== 3) return null;
  const yr = parseInt(parts[0], 10) + parseInt(m[1], 10);
  return `${yr}-${parts[1]}-${parts[2]}`;
}

/** Exported for INV-SERIAL-01: the printed invoice shows ESC's REAL recorded
 *  warranty dates (same derivation the Equipment tab uses), never a recompute. */
export function parseWarranty(row: EquipmentRow): { labor: string | null; parts: string | null; effective: string | null } {
  const n = (row.notes ?? "").toUpperCase();
  let labor: string | null = null;
  let parts: string | null = null;

  // 1. Absolute dates in notes ("LABOR/PARTS WARRANTY EXPIRES 5/22/2028").
  const lm = n.match(/LABOR WARRANTY EXPIRES\s+([\d/]+)/);
  const pm = n.match(/PARTS WARRANTY EXPIRES\s+([\d/]+)/);
  if (lm) labor = parseDateLoose(lm[1]);
  if (pm) parts = parseDateLoose(pm[1]);

  // 2. Relative TERMS in custom fields ("1 Year" labor / "6 Years" parts),
  //    computed off the install date. (ESC's other warranty convention.)
  if (!labor) labor = termToExpiry(row.custom_1, row.install_date);
  if (!parts) parts = termToExpiry(row.custom_2, row.install_date);

  // 3. Column fallback — only if plausible (>= 2000 and >= install).
  let effective = parts ?? labor ?? null;
  if (!effective && row.warranty_date) {
    const w = row.warranty_date;
    const okYear = w >= "2000-01-01";
    const afterInstall = !row.install_date || w >= row.install_date;
    if (okYear && afterInstall) effective = w;
  }
  return { labor, parts, effective };
}

function completeness(r: EquipmentRow): number {
  return [r.serial, r.model, r.install_date, r.warranty_date, r.notes, r.eq_type, r.equip_loc]
    .filter((v) => v && String(v).trim().length > 0).length;
}
function normSerial(s: string | null): string {
  return (s ?? "").toUpperCase().replace(/[^A-Z0-9]/g, "");
}
function yearOf(d: string | null): number | null {
  if (!d) return null;
  const y = parseInt(d.slice(0, 4), 10);
  return Number.isFinite(y) && y > 1950 ? y : null;
}

/**
 * DATE-TRUTH (34i82, Seth 2026-10-20: "show the exact install date if we have
 * it — if it's 1/9/00 or whatever the default date is we wouldn't want that").
 * What ESC's install_date really holds: 1900-01-01 on ~97,000 rows (its blank),
 * a few hundred 1970-01-01 / 1999-12-31 placeholders, and thousands of
 * "YYYY-01-01" entries where the office knew the YEAR only. So:
 *   null            → no usable date (say nothing)
 *   exact: false    → the year is real, the day is not — print "2014"
 *   exact: true     → print the day — "Jun 14, 2024"
 */
export function installDateInfo(d: string | null | undefined): { iso: string; year: number; exact: boolean } | null {
  if (!d) return null;
  const iso = String(d).slice(0, 10);
  const y = parseInt(iso.slice(0, 4), 10);
  if (!Number.isFinite(y) || y <= 1950 || y > new Date().getFullYear() + 1) return null;
  if (iso === "1970-01-01" || iso === "1999-12-31") return null;
  return { iso, year: y, exact: iso.slice(5) !== "01-01" };
}
/** "Jun 14, 2024" · "Jun 2024" (style month) · "2014" when only the year is real · null when there is no date. */
export function installDateLabel(d: string | null | undefined, style: "day" | "month" | "year" = "day"): string | null {
  const i = installDateInfo(d);
  if (!i) return null;
  if (!i.exact || style === "year") return String(i.year);
  const dt = new Date(i.iso + "T00:00:00");
  if (Number.isNaN(dt.getTime())) return String(i.year);
  return style === "day"
    ? dt.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : dt.toLocaleDateString("en-US", { month: "short", year: "numeric" });
}

// ============================================================================
// Per-row classification (tier + category) WITHOUT the per-customer dedup /
// lifecycle resolution that buildEquipmentView does. Company-wide reporting
// (REPORT-01 equipment installs) needs to classify each install record on its
// own — running the full lifecycle pass across many customers would wrongly
// mark units "replaced" by comparing different households. Exported so the
// Reports surface and the Customer 360 Equipment box share ONE classifier.
// ============================================================================
export function classifyEquipment(
  row: EquipmentRow,
  aiOverrides?: Record<string, EquipCategory>,
): {
  tier: EquipTier;
  category: EquipCategory;
  categoryLabel: string;
} {
  let tier: EquipTier;
  let category: EquipCategory;
  if (isMaterial(row)) { tier = "material"; category = "material"; }
  else if (isConsumable(row)) { tier = "consumable"; category = "consumable"; }
  else {
    category = detectCategory(row);
    // EQUIP-360-AI: when the deterministic rules can't place a model ("other"),
    // fall back to the AI classifier's verdict (equipment_model_class) so the
    // long tail (e.g. a Bryant 581 rooftop) gets a real category, not "Equipment".
    if (category === "other" && aiOverrides) {
      const key = `${(row.mfg ?? "").toUpperCase().trim()}|${(row.model ?? "").toUpperCase().trim()}`;
      const ai = aiOverrides[key];
      if (ai && ai !== "other") category = ai;
    }
    tier = tierFor(category);
  }
  return { tier, category, categoryLabel: categoryLabelFor(category) };
}

// ============================================================================
// Main: raw rows -> classified, deduped, lifecycle-resolved view.
// ============================================================================
export function buildEquipmentView(
  rows: EquipmentRow[],
  aiOverrides?: Record<string, EquipCategory>,
  lifecycleOverrides?: Map<string, EquipOverride>,
): EquipmentView {
  // 1. Classify every row (shared per-row classifier).
  const classified: ClassifiedEquip[] = rows.map((row) => {
    const { tier, category, categoryLabel } = classifyEquipment(row, aiOverrides);
    const w = parseWarranty(row);
    return {
      ...row, tier, category, categoryLabel,
      installYear: yearOf(row.install_date),
      warrantyLabor: w.labor, warrantyParts: w.parts, warrantyEffective: w.effective,
    };
  });

  // 2. Dedupe units by serial (keep the most-complete; mark the rest).
  // MIRROR-EQUIP-DELETE-01: a row the ESC sync has stopped returning (deleted or
  // re-keyed in ESC — the sweep marks it removed / deleted_in_esc) is set aside
  // FIRST, so it can never win the dedupe over its surviving ESC twin; it lands
  // in the replaced bucket labelled "deleted in ESC".
  const escDeleted: ClassifiedEquip[] = [];
  const units = classified.filter((c) => c.tier === "system" || c.tier === "auxiliary" || c.tier === "other").filter((u) => {
    const ov = lifecycleOverrides?.get(u.counter);
    if (ov?.status === "removed" && ov.removal_reason === "deleted_in_esc") {
      u.deletedInEsc = true; u.replaced = true; u.overrideSet = true; u.replacedReason = "deleted_in_esc";
      if (ov.removal_date) u.replacedDate = ov.removal_date;
      escDeleted.push(u);
      return false;
    }
    return true;
  });
  const bySerial = new Map<string, ClassifiedEquip[]>();
  const noSerial: ClassifiedEquip[] = [];
  for (const u of units) {
    const ns = normSerial(u.serial);
    if (ns.length >= 4) {
      if (!bySerial.has(ns)) bySerial.set(ns, []);
      bySerial.get(ns)!.push(u);
    } else noSerial.push(u);
  }
  let keepers: ClassifiedEquip[] = [...noSerial];
  const dupes: ClassifiedEquip[] = [];
  for (const group of bySerial.values()) {
    if (group.length === 1) { keepers.push(group[0]); continue; }
    const sorted = [...group].sort((a, b) => completeness(b) - completeness(a));
    keepers.push(sorted[0]);
    for (const d of sorted.slice(1)) { d.duplicateOf = sorted[0].counter; dupes.push(d); }
  }
  // Two units that BOTH carry a named room, and the rooms differ, are separate
  // systems (EQUIP-AREA-LIFE-01, the Gordon Johnson case) — used by 2b and 3.
  const areaOf = (u: ClassifiedEquip) => (u.equip_loc ?? "").trim().toLowerCase();
  const separateRooms = (a: ClassifiedEquip, b: ClassifiedEquip) => {
    const x = areaOf(a), y = areaOf(b);
    return x !== "" && y !== "" && x !== y;
  };

  // 2b. DOUBLE ENTRY (34i83, Seth 2026-10-20: "On 11/18/15 the technician
  //     charged for it and the customer paid for it, but we didn't return to
  //     install it until 12/9/15 — it was put on both invoices"). The same
  //     model in the same category, dated a few days to a few weeks apart, is
  //     ONE unit on two invoices — the sale and the install — not a unit that
  //     replaced itself. The LATER record is the unit (that is when it went
  //     in); the earlier folds into it as a duplicate and is remembered as the
  //     sale date / invoice. Never folded: the same day (real twins — the
  //     Cress View double-600A), two named rooms that differ, or two real
  //     serials that differ (a warranty swap is a replacement, not a re-bill).
  const DOUBLE_ENTRY_DAYS = 90;
  const byModelCat = new Map<string, ClassifiedEquip[]>();
  for (const k of keepers) {
    const bm = baseModel(k.model);
    if (!bm || !k.install_date || !yearOf(k.install_date)) continue;
    const key = `${k.category}|${bm}`;
    if (!byModelCat.has(key)) byModelCat.set(key, []);
    byModelCat.get(key)!.push(k);
  }
  const folded = new Set<string>();
  const daysBetween = (a: ClassifiedEquip, b: ClassifiedEquip) => (Date.parse(b.install_date!) - Date.parse(a.install_date!)) / 86400000;
  for (const list of byModelCat.values()) {
    if (list.length < 2) continue;
    list.sort((a, b) => a.install_date!.localeCompare(b.install_date!));
    for (let i = 0; i < list.length - 1; i++) {
      const early = list[i], late = list[i + 1];
      if (folded.has(early.counter)) continue;
      const gap = daysBetween(early, late);
      if (!(gap > 0 && gap <= DOUBLE_ENTRY_DAYS)) continue;
      // A lone PAIR only. A run of the same model every few weeks (a builder
      // account's twenty AA600A humidifiers across twenty houses) is twenty
      // units, not one sale billed twenty times — never chain-fold it.
      const prevClose = i > 0 && daysBetween(list[i - 1], early) <= DOUBLE_ENTRY_DAYS;
      const nextClose = i + 2 < list.length && daysBetween(late, list[i + 2]) <= DOUBLE_ENTRY_DAYS;
      if (prevClose || nextClose) continue;
      if (separateRooms(early, late)) continue;
      const se = normSerial(early.serial), sl = normSerial(late.serial);
      if (se.length >= 4 && sl.length >= 4 && se !== sl) continue;
      early.duplicateOf = late.counter;
      late.soldDate = early.install_date;
      late.soldInvoice = early.invoice ?? null;
      folded.add(early.counter);
      dupes.push(early);
    }
  }
  if (folded.size > 0) keepers = keepers.filter((k) => !folded.has(k.counter));

  // 3. Lifecycle: within a category, a unit is "replaced" if a same-category
  //    unit exists with an install year >= ~1 year newer. Conservative so a
  //    legit second system of the same type isn't wrongly retired.
  const byCat = new Map<EquipCategory, ClassifiedEquip[]>();
  for (const k of keepers) {
    if (!byCat.has(k.category)) byCat.set(k.category, []);
    byCat.get(k.category)!.push(k);
  }
  // (a) Explicit removal NOTE = strongest signal a unit is gone, regardless of
  // category math. Phrasings vary in ESC: "Removed on 6/25/19 under warranty",
  // "*Removed under warranty on 6/5/2026*", "removed and replaced". Match
  // REMOVED followed (within a few words) by a date / on / under / warranty /
  // replaced cue — so a stray "removed the filter" note can't retire a whole
  // system. (The old `\bREMOVED ON\b` missed the "Removed under warranty on…"
  // phrasing — the same-year warranty-swap case.)
  const REMOVAL_NOTE = /\bREMOVED\b[\s\S]{0,30}?(\bON\b|\bUNDER\b|\bWARRANTY\b|\bREPLACED\b|\d{1,2}\/\d{1,2})/;
  for (const k of keepers) {
    if (REMOVAL_NOTE.test((k.notes ?? "").toUpperCase())) k.replaced = true;
  }
  // EQUIP-PLACEHOLDER-01 (34i101, 5112 Arden): ESC writes a PLACEHOLDER row for each
  // unit an agreement covers when nobody typed the model — model "OO", "0", ".", "n/a".
  // Craig's Dec 2017 "OO" air conditioner (the 2-Year Plan's cooling line) was dated
  // newer than the addition's 2013 13ACX024 and quietly retired it. A placeholder is a
  // stand-in for a unit already on the record, never evidence of a NEW one: it can't
  // retire anything, and when a real unit of its kind is on the record it folds into
  // the record as a duplicate.
  const PLACEHOLDER_MODEL = /^(?:[.oO0]{1,3}|n\/?a|none|unknown|unk|\?+|tbd|-+|x+)?$/i;
  const isPlaceholder = (u: ClassifiedEquip) => PLACEHOLDER_MODEL.test((u.model ?? "").trim());
  // Category + date lifecycle (newest by FULL install date, not just year).
  // EQUIP-AREA-LIFE-01 (Seth 2026-10-02, the Gordon Johnson case): the
  // comparison is AREA-AWARE now. Gordon's 2026 Main-floor furnace was
  // silently "replacing" his 2013 FOYER furnace — a different system serving
  // a different part of the house — which made a two-system home read as
  // one. Rule: two units that BOTH carry a named room, and the rooms differ,
  // are separate systems — neither can retire the other. A blank-area unit
  // keeps today's behavior (old rows often lack the room), so single-system
  // homes with sloppy area data still clean up exactly as before. This also
  // protects same-model twins (the Cress View double-600A landmine).
  for (const [, list] of byCat) {
    const dated = list.filter((u) => u.install_date);
    if (dated.length === 0) continue; // can't infer if nothing in the category is dated
    for (const u of list) {
      if (u.replaced) continue;
      // Only units that could be the SAME physical slot compete — same room,
      // or at least one side without a room on record.
      const rivals = dated.filter((v) => v !== u && !separateRooms(v, u) && !isPlaceholder(v));
      if (rivals.length === 0) continue;
      const newest = rivals.reduce((a, b) => ((a.install_date ?? "") >= (b.install_date ?? "") ? a : b));
      if ((newest.install_date ?? "") <= (u.install_date ?? "")) continue; // u is the newest in its slot
      // (b) SAME-MODEL supersession: a unit with the same base model as a
      //     strictly-newer-dated unit is the one swapped out — even within the
      //     same calendar year. baseModel() collapses build-revision suffixes so
      //     ML13KC1-018-230A02 (removed) == …A04 (installed): same unit, new build.
      const newerSameModel = rivals.find(
        (v) => v.install_date! > (u.install_date ?? "￿") &&
               baseModel(v.model) !== "" && baseModel(v.model) === baseModel(u.model),
      );
      // (c) Conservative cross-model rules (unchanged): dated >= ~1yr older than
      //     the newest, or an UNDATED unit sitting beside a dated one.
      const olderByYear = u.installYear !== null && newest.installYear !== null &&
               newest.installYear - u.installYear >= 1;
      const undatedBesideDated = u.install_date === null;
      if (newerSameModel || olderByYear || undatedBesideDated) {
        u.replaced = true;
        u.replacedByModel = (newerSameModel ?? newest).model;
        u.replacedDate = (newerSameModel ?? newest).install_date;
      }
    }
  }

  // 3a. A placeholder beside a real, current unit of its kind folds into the record.
  const placeholderFold = new Set<string>();
  for (const [, list] of byCat) {
    const real = list.filter((v) => !isPlaceholder(v) && !v.replaced);
    if (!real.length) continue;
    for (const u of list) {
      if (!isPlaceholder(u) || u.replaced) continue;
      u.duplicateOf = real[0].counter;
      placeholderFold.add(u.counter);
      dupes.push(u);
    }
  }
  if (placeholderFold.size > 0) keepers = keepers.filter((k) => !placeholderFold.has(k.counter));

  // 3b. Human overrides BEAT the derived guess (EQUIP-LIFECYCLE-02): the office
  //     can mark a unit removed (warranty/breakdown/upgrade) or un-retire one the
  //     rules wrongly retired. Authoritative over the heuristics above.
  if (lifecycleOverrides && lifecycleOverrides.size > 0) {
    for (const k of keepers) {
      const ov = lifecycleOverrides.get(k.counter);
      if (!ov) continue;
      // EQUIP-EDIT-FIX-01: a field-correction row with NO status is not a
      // lifecycle statement — the derived guess stands untouched.
      if (ov.status === "removed") {
        k.overrideSet = true;
        k.replaced = true;
        k.replacedReason = ov.removal_reason ?? null;
        k.deletedInEsc = ov.removal_reason === "deleted_in_esc";
        if (ov.removal_date) k.replacedDate = ov.removal_date;
        if (ov.replaced_by_model) k.replacedByModel = ov.replaced_by_model;
      } else if (ov.status === "active") {
        k.overrideSet = true;
        k.replaced = false; // force active — corrects a wrong derive
        k.replacedReason = null;
      }
    }
  }

  // 4. Bucket.
  const active = keepers.filter((k) => !k.replaced);
  const replaced = [...keepers.filter((k) => k.replaced), ...dupes, ...escDeleted];
  const systems = active.filter((k) => k.tier === "system" || k.tier === "other");
  const auxiliary = active.filter((k) => k.tier === "auxiliary");
  const consumables = classified.filter((c) => c.tier === "consumable");
  const materials = classified.filter((c) => c.tier === "material");

  const byInstallDesc = (a: ClassifiedEquip, b: ClassifiedEquip) =>
    (b.install_date ?? "").localeCompare(a.install_date ?? "");
  systems.sort(byInstallDesc);
  auxiliary.sort(byInstallDesc);
  replaced.sort(byInstallDesc);

  return {
    systems, auxiliary, consumables, materials, replaced,
    counts: {
      systems: systems.length, auxiliary: auxiliary.length,
      consumables: consumables.length, materials: materials.length, replaced: replaced.length,
    },
  };
}
